import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AssistantAuthorization,
  AuthorizeAssistantConnectionError,
  AuthorizeAssistantError,
  AuthorizeAssistantForbiddenError,
  AuthorizeAssistantRateLimitedError,
  AuthorizeAssistantSessionExpiredError,
  AuthorizeAssistantUntrustedInstanceError,
  AuthorizeAssistantUseCase,
  CheckValidSessionUseCase,
  CliAuthorizationRequest,
  GetLoggedUserUsecase,
  GetMcpClientRequestUseCase,
  GetOrganizationsUseCase,
  LogOutUseCase,
  McpClientRequest,
  Organization,
  showsIdentityProvider
} from '@core/core';
import { CookieService, ORGANIZATION_COOKIE } from '@core/auth/infrastructure/cookie.service';
import { Failure, Success, cliCallbackUrl, urlWithoutQuery } from '@sdk/sdk';
import * as Sentry from '@sentry/angular';
import { OrganizationRedirectHelper } from '../organization-list/organization-redirect-helper';
import { europeCliUrl } from './cli-region';
import { mcpCallbackUrl } from './mcp-callback';

type CliAuthorizeStatus =
  | 'loading'
  | 'invalidLink'
  | 'loadFailed'
  | 'noOrganizations'
  | 'noInstanceOrganizations'
  | 'ready'
  | 'authorizing'
  | 'returning'
  | 'showingCode'
  | 'cancelled';

/**
 * Where an assistant sends the user to connect it to Sytex. The user confirms
 * with the session they already have here; the page then asks the
 * organization's instance for a one-time code and hands it to the assistant:
 * a CLI through its local callback or as text to paste, an MCP client through
 * the Sytex MCP server's fixed callback.
 */
@Component({
  selector: 'app-cli-authorize',
  templateUrl: './cli-authorize.component.html',
  styleUrls: ['./cli-authorize.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CliAuthorizeComponent implements OnInit {
  private _request: CliAuthorizationRequest | null = null;

  status = signal<CliAuthorizeStatus>('loading');
  email = signal('');
  deviceName = signal('');
  isMcp = signal(false);
  mcpClient = signal<McpClientRequest | undefined>(undefined);
  /** An app opened through its link scheme, named after it: `cursor` is Cursor. */
  destinationAppName = computed(() => {
    const scheme = this.mcpClient()?.destinationHost ?? '';
    return scheme.charAt(0).toUpperCase() + scheme.slice(1);
  });
  organizations = signal<Organization[]>([]);
  selectedOrganization = signal<Organization | undefined>(undefined);
  isPickingOrganization = signal(false);
  authorization = signal<AssistantAuthorization | undefined>(undefined);
  errorMessage = signal('');
  isCopied = signal(false);
  canSignInWithOrganization = signal(false);
  isSigningOut = signal(false);
  canAuthorize = computed(
    () => !!this.selectedOrganization() && !this.isPickingOrganization() && !this.isSigningOut()
  );
  /** An MCP grant is bound to the one organization chosen, so it brings no others along. */
  otherOrganizationCount = computed(() => (this.isMcp() ? 0 : this._otherInstanceOrganizationCount()));
  expiresInMinutes = computed(() => this.authorization()?.expiresInMinutes ?? 0);
  readonly europeUrl = europeCliUrl(window.location.href);
  private readonly _mcpCallbackUrl = mcpCallbackUrl();

  constructor(
    private _checkSessionUseCase: CheckValidSessionUseCase,
    private _getLoggedUserUsecase: GetLoggedUserUsecase,
    private _getOrganizationsUseCase: GetOrganizationsUseCase,
    private _getMcpClientRequestUseCase: GetMcpClientRequestUseCase,
    private _authorizeAssistantUseCase: AuthorizeAssistantUseCase,
    private _logOutUseCase: LogOutUseCase,
    private _cookieService: CookieService,
    private _organizationRedirect: OrganizationRedirectHelper,
    private _route: ActivatedRoute,
    private _router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    const query = this._route.snapshot.queryParamMap;
    this._request = CliAuthorizationRequest.fromQuery(key => query.get(key));
    const hasNoMcpServer = this._request?.isMcp === true && this._mcpCallbackUrl === null;
    if (!this._request || hasNoMcpServer) {
      this._showInvalidLink();
      return;
    }
    if (this._request.isMcp && this._mcpCallbackUrl) {
      const mcpClient = await this._getMcpClient(this._mcpCallbackUrl, this._request.state);
      if (!mcpClient) {
        this.status.set('invalidLink');
        return;
      }
      this.mcpClient.set(mcpClient);
      this._request = this._request.withName(mcpClient.clientName);
    }
    this.deviceName.set(this._request.name);
    this.isMcp.set(this._request.isMcp);

    const session = await this._checkSessionUseCase.execute();
    if (session instanceof Failure) {
      this._goToLogin();
      return;
    }

    const loggedUser = await this._getLoggedUserUsecase.execute();
    if (loggedUser instanceof Success) {
      this.email.set(loggedUser.value.email);
    }

    await this._loadOrganizations();
  }

  async retry(): Promise<void> {
    this.status.set('loading');
    await this._loadOrganizations();
  }

  selectOrganization(organization: Organization): void {
    this.selectedOrganization.set(organization);
    this.isPickingOrganization.set(false);
    this.errorMessage.set('');
    this.canSignInWithOrganization.set(false);
  }

  changeOrganization(): void {
    this.isPickingOrganization.set(true);
  }

  isSelected(organization: Organization): boolean {
    return this.selectedOrganization() === organization;
  }

  showIdentityProvider(organization: Organization): boolean {
    const organizations = this.organizations();
    return showsIdentityProvider(organization, organizations);
  }

  async authorize(): Promise<void> {
    const organization = this.selectedOrganization();
    const request = this._request;
    const isReady = this.status() === 'ready' && !this.isSigningOut();
    if (!organization || !request || !isReady) {
      return;
    }

    this.status.set('authorizing');
    this.errorMessage.set('');
    this.canSignInWithOrganization.set(false);
    const result = await this._authorizeAssistantUseCase.execute(organization, request);

    if (result instanceof Failure) {
      if (result.error instanceof AuthorizeAssistantSessionExpiredError) {
        this._goToLogin();
        return;
      }
      this._reportFailure(result.error, organization);
      const message = this._errorMessageFor(result.error);
      this.errorMessage.set(message);
      // A session from another login method: the peer organization list would
      // have sent the user through the identity provider, so offer that here.
      const isForbidden = result.error instanceof AuthorizeAssistantForbiddenError;
      this.canSignInWithOrganization.set(isForbidden && organization.ssoIsMandatory);
      this.status.set('ready');
      return;
    }

    const authorization = result.value;
    if (request.isMcp) {
      this.status.set('returning');
      this._returnToMcpServer({
        code: authorization.code,
        instance: authorization.instance,
        state: request.state
      });
      return;
    }
    if (request.port === null) {
      this.authorization.set(authorization);
      this.status.set('showingCode');
      return;
    }

    const callbackUrl = cliCallbackUrl(request.port, {
      code: authorization.code,
      instance: authorization.instance,
      state: request.state
    });
    if (!callbackUrl) {
      this._showInvalidLink();
      return;
    }
    this.status.set('returning');
    window.location.href = callbackUrl;
  }

  /**
   * Tells a listening CLI or the MCP server the user said no, so it stops waiting instead of
   * timing out. `access_denied` is the OAuth 2.0 code for exactly this.
   */
  cancel(): void {
    const request = this._request;
    this.status.set('cancelled');
    if (request?.isMcp) {
      this._returnToMcpServer({ error: 'access_denied', state: request.state });
      return;
    }
    if (!request || request.port === null) {
      return;
    }
    const callbackUrl = cliCallbackUrl(request.port, { error: 'access_denied', state: request.state });
    if (!callbackUrl) {
      return;
    }
    window.location.href = callbackUrl;
  }

  /** Signs out and opens the login page, keeping this request as the login's `r`. */
  async useAnotherAccount(): Promise<void> {
    this.isSigningOut.set(true);
    await this._logOutUseCase.execute();
    this._goToLogin();
  }

  /**
   * Signs in again through the organization's identity provider, the way the
   * organization list does for an organization that requires it, and comes
   * back here afterwards.
   */
  signInWithOrganization(): void {
    const organization = this.selectedOrganization();
    if (!organization) {
      return;
    }
    this._organizationRedirect.goToIdentityProvider(organization.identityProviderName, window.location.href);
  }

  async copyCode(): Promise<void> {
    const authorization = this.authorization();
    if (!authorization) {
      return;
    }
    // Refused outside a secure context or without clipboard permission; the
    // code stays on screen and selectable, so a refusal just shows no tick.
    const isCopied = await navigator.clipboard.writeText(authorization.pasteCode).then(
      () => true,
      () => false
    );
    this.isCopied.set(isCopied);
  }

  private async _loadOrganizations(): Promise<void> {
    const organizations = await this._getOrganizationsUseCase.execute();
    if (organizations instanceof Failure) {
      this.status.set('loadFailed');
      return;
    }
    if (organizations.value.length === 0) {
      this.status.set('noOrganizations');
      return;
    }

    const mcpClient = this.mcpClient();
    const usableOrganizations = mcpClient
      ? organizations.value.filter(organization => mcpClient.isOnInstance(organization))
      : organizations.value;
    if (usableOrganizations.length === 0) {
      this.status.set('noInstanceOrganizations');
      return;
    }

    this.organizations.set(usableOrganizations);
    const preselected = this._preselectOrganization(usableOrganizations);
    this.selectedOrganization.set(preselected);
    this.isPickingOrganization.set(!preselected);
    this.status.set('ready');
  }

  /**
   * What the MCP server sealed into the link's state, asked of the server the
   * page returns to. Null when the server does not vouch for the state.
   */
  private async _getMcpClient(callbackUrl: string, state: string): Promise<McpClientRequest | null> {
    const serverOrigin = new URL(callbackUrl).origin;
    const result = await this._getMcpClientRequestUseCase.execute(serverOrigin, state);
    if (result instanceof Failure) {
      return null;
    }
    return result.value;
  }

  /** The fixed callback, never an address from the link. */
  private _returnToMcpServer(query: Record<string, string>): void {
    if (!this._mcpCallbackUrl) {
      return;
    }
    const url = new URL(this._mcpCallbackUrl);
    Object.entries(query).forEach(([key, value]) => url.searchParams.set(key, value));
    window.location.href = url.toString();
  }

  /**
   * The organization the user last entered (the `Organization` cookie every
   * Sytex app shares), or the only one they have. Several and none remembered:
   * the user picks. An MCP connection is bound to the organization for good,
   * so with several the user always picks.
   */
  private _preselectOrganization(organizations: Organization[]): Organization | undefined {
    if (this._request?.isMcp) {
      return organizations.length === 1 ? organizations[0] : undefined;
    }
    const lastOrganizationId = this._cookieService.getCookie(ORGANIZATION_COOKIE);
    const lastOrganization = organizations.find(organization => organization.id === lastOrganizationId);
    if (lastOrganization) {
      return lastOrganization;
    }
    return organizations.length === 1 ? organizations[0] : undefined;
  }

  /**
   * The grant is not bound to the chosen organization: the instance hands the
   * assistant every organization there where the user may act, so the screen
   * says how many others come with it. An organization reached through several
   * identity providers is listed once per provider, hence the distinct ids.
   */
  private _otherInstanceOrganizationCount(): number {
    const selected = this.selectedOrganization();
    if (!selected) {
      return 0;
    }
    const sameInstance = this.organizations().filter(
      organization => organization.url.host === selected.url.host
    );
    const ids = new Set(sameInstance.map(organization => organization.id));
    return ids.size - 1;
  }

  /** Login first, then back here with the same query through the usual `r`. */
  private _goToLogin(): void {
    this._router.navigate(['/login'], { queryParams: { r: window.location.href } });
  }

  private _showInvalidLink(): void {
    this.status.set('invalidLink');
    this._report('CLI authorization link refused', {});
  }

  /** Never the code, state, code challenge or token: only what kind of failure and where. */
  private _reportFailure(error: AuthorizeAssistantError, organization: Organization): void {
    const kind = this._failureKind(error);
    this._report('CLI authorization failed', { kind, host: organization.url.host });
  }

  /**
   * Reports this page without its query, which carries the code challenge and
   * the CLI's state: Sentry would otherwise attach the full URL to the event.
   */
  private _report(message: string, extra: Record<string, string>): void {
    const url = urlWithoutQuery(window.location.href);
    Sentry.withScope(scope => {
      scope.addEventProcessor(event => ({ ...event, request: { ...event.request, url } }));
      Sentry.captureMessage(message, { level: 'warning', extra });
    });
  }

  // Fixed names: class names do not survive the production build's minifier.
  private _failureKind(error: AuthorizeAssistantError): string {
    if (error instanceof AuthorizeAssistantForbiddenError) {
      return 'forbidden';
    }
    if (error instanceof AuthorizeAssistantRateLimitedError) {
      return 'rateLimited';
    }
    if (error instanceof AuthorizeAssistantConnectionError) {
      return 'connection';
    }
    if (error instanceof AuthorizeAssistantUntrustedInstanceError) {
      return 'untrustedInstance';
    }
    return 'unknown';
  }

  private _errorMessageFor(error: AuthorizeAssistantError): string {
    if (error instanceof AuthorizeAssistantForbiddenError) {
      return $localize`Sytex did not allow the connection in this organization. Check that you have a license there and that you signed in the way your organization requires.`;
    }
    if (error instanceof AuthorizeAssistantRateLimitedError) {
      return $localize`Too many attempts. Wait a few minutes and try again.`;
    }
    if (error instanceof AuthorizeAssistantUntrustedInstanceError) {
      return $localize`This organization cannot connect an assistant. Contact Sytex support.`;
    }
    return $localize`We could not reach Sytex. Please try again.`;
  }
}
